// Admin email templates - list + edit form with sandboxed preview.
// The 3-tier fallback runs server-side; this UI lets admins author
// templates at any tier (global / per-organization / per-organization-and-pointType).
import './site';
import { loadConfig } from '../envConfig';
import { api } from './api';
import { requirePlatformAdmin, signOut } from './lib/auth';
import { sanitizeEmailHtml } from './lib/sanitizeHtml';
import { confirmModal, setButtonBusy } from './admin/shell';

interface EmailTemplate {
  id: string;
  name: string;
  kind: string;
  organizationId: string | null;
  pointType: string | null;
  subject: string;
  body: string;
  lastUpdated: string;
}
interface OrganizationSummary { id: string; name: string; }

const KINDS = [
  'account_created_by_admin',
  'confirmation_requester',
  'confirmation_connector',
  'new_request_admin',
  'contact_message_admin',
  'application_admin',
  'request_edited',
  'rejection',
  'signing_invite',
  'signing_code',
  'approval_operator',
  'approval_municipality',
  'install_party_notify_only',
  'install_party_key_delivery',
  'final_go_requester',
] as const;

// Placeholders the backend supplies per kind - mirrors the variable
// dictionaries in RequestWorkflowService.BuildVariables and the three
// workflow admin controllers. Keep in sync when a controller adds a variable.
const REQUEST_PLACEHOLDERS = [
  'requestId', 'projectName', 'organisationName',
  'contactName', 'contactEmail', 'contactPhone',
  'connectorName', 'connectorEmail', 'connectorPhone',
  'pointName', 'pointAddress', 'pointCity', 'pointsList',
  'startAt', 'endAt', 'maxCurrentA', 'currencySymbol', 'brandTitle',
];
const SIGNING_PLACEHOLDERS = [
  'requestId', 'projectName', 'organisationName',
  'signerEmail', 'expiresAt', 'signingLink', 'code', 'brandTitle',
];
const APPROVAL_PLACEHOLDERS = [
  'requestId', 'projectName', 'organisationName',
  'recipientEmail', 'recipientName', 'expiresAt', 'approvalLink', 'brandTitle',
];
const INSTALL_PARTY_PLACEHOLDERS = [
  'requestId', 'projectName', 'organisationName',
  'contactName', 'contactEmail', 'contactPhone',
  'connectorName', 'connectorEmail', 'connectorPhone',
  'pointName', 'pointAddress', 'pointCity', 'pointsList',
  'servicePartyIdCodes', 'startAt', 'endAt', 'installerName', 'brandTitle',
];
const PLACEHOLDERS_BY_KIND: Record<string, string[]> = {
  account_created_by_admin: ['name', 'email', 'temporaryPassword'],
  confirmation_requester: REQUEST_PLACEHOLDERS,
  confirmation_connector: REQUEST_PLACEHOLDERS,
  new_request_admin: REQUEST_PLACEHOLDERS,
  contact_message_admin: [
    'name', 'email', 'organisation', 'phone',
    'topic', 'message', 'createdAt', 'brandTitle',
  ],
  application_admin: [
    'organisation', 'name', 'email', 'phone',
    'message', 'createdAt', 'brandTitle',
  ],
  request_edited: [...REQUEST_PLACEHOLDERS, 'changesSummary'],
  rejection: REQUEST_PLACEHOLDERS,
  signing_invite: SIGNING_PLACEHOLDERS,
  signing_code: SIGNING_PLACEHOLDERS,
  approval_operator: APPROVAL_PLACEHOLDERS,
  approval_municipality: APPROVAL_PLACEHOLDERS,
  install_party_notify_only: INSTALL_PARTY_PLACEHOLDERS,
  install_party_key_delivery: INSTALL_PARTY_PLACEHOLDERS,
  final_go_requester: [
    'requestId', 'projectName', 'organisationName', 'contactName',
    'connectorName', 'connectorEmail', 'connectorPhone',
    'pointName', 'pointAddress', 'pointCity', 'pointsList',
    'startAt', 'endAt', 'brandTitle',
  ],
};

const POINT_TYPES = ['', 'PowerStation', 'ChargingStation', 'WaterPoint'] as const;

await loadConfig();
const me = await requirePlatformAdmin();
const isOrganization = me.role === 'organization';

const userNameEl = document.getElementById('user-name');
if (userNameEl) userNameEl.textContent = me.name || me.email;
document.getElementById('logout-btn')?.addEventListener('click', async (e) => {
  e.preventDefault();
  await signOut();
  window.location.replace('/login.html');
});

let templates: EmailTemplate[] = [];
let organizations: OrganizationSummary[] = [];
let editingId: string | null = null;

async function loadOrganizations(): Promise<void> {
  if (isOrganization) return;
  try {
    const client = await api();
    const { data } = await client.get<OrganizationSummary[]>('/api/admin/organizations');
    organizations = data;
  } catch { /* silent */ }
}

async function loadTemplates(): Promise<void> {
  const client = await api();
  const { data } = await client.get<EmailTemplate[]>('/api/admin/email-templates');
  templates = data;
  renderList();
}

function renderList(): void {
  const list = document.getElementById('tpl-list');
  if (!list) return;
  if (templates.length === 0) {
    list.innerHTML = '<div style="padding:24px;color:var(--sk-muted);">No templates yet - create one with "+ Nieuwe template".</div>';
    return;
  }
  list.innerHTML = templates.map((t) => `
    <button type="button" class="tpl-card" data-id="${escapeHtml(t.id)}" style="display:block;width:100%;text-align:left;padding:14px 18px;border:none;border-bottom:1px solid var(--sk-border-soft,#eee);background:${editingId === t.id ? 'var(--sk-blue-50,#e9eef9)' : 'transparent'};cursor:pointer;">
      <div style="font-weight:600;">${escapeHtml(t.name)}</div>
      <div style="font-size:12px;color:var(--sk-muted);">${escapeHtml(t.kind)} · ${escapeHtml(t.organizationId ?? 'global')}${t.pointType ? ' · ' + escapeHtml(t.pointType) : ''}</div>
    </button>`).join('');
  list.querySelectorAll<HTMLButtonElement>('.tpl-card').forEach((btn) => {
    btn.addEventListener('click', () => openEdit(btn.dataset.id!));
  });
}

function openEdit(id: string): void {
  const t = templates.find((x) => x.id === id);
  if (!t) return;
  editingId = id;
  renderList();
  renderForm(t);
}

function renderNew(): void {
  editingId = null;
  renderList();
  renderForm({
    id: '',
    name: '',
    kind: 'confirmation_requester',
    organizationId: isOrganization ? me.id : null,
    pointType: null,
    subject: '',
    body: '',
    lastUpdated: '',
  });
}

function renderForm(t: EmailTemplate): void {
  const panel = document.getElementById('tpl-edit');
  if (!panel) return;
  const isNew = editingId === null;

  panel.innerHTML = `
    <form id="tpl-form" style="display:grid;grid-template-columns:1fr 1fr;gap:16px;padding:24px;">
      <div style="grid-column:1 / -1;display:flex;justify-content:space-between;align-items:center;">
        <h2 style="margin:0;">${isNew ? 'New template' : 'Edit template'}</h2>
        ${!isNew ? `<button type="button" id="tpl-delete" class="btn btn--danger" style="padding:6px 14px;font-size:13px;">Delete</button>` : ''}
      </div>

      <label style="display:block;${isNew ? '' : 'opacity:.6;'}">
        <span style="display:block;font-size:13px;color:var(--sk-muted);margin-bottom:4px;">Id (slug)</span>
        <input name="id" type="text" required ${isNew ? '' : 'readonly'} value="${escapeHtml(t.id)}" placeholder="e.g. amsterdam-shore-power-approval" style="width:100%;padding:8px;border:1px solid var(--sk-border,#ddd);border-radius:6px;"/>
      </label>

      <label style="display:block;">
        <span style="display:block;font-size:13px;color:var(--sk-muted);margin-bottom:4px;">Name</span>
        <input name="name" type="text" required value="${escapeHtml(t.name)}" style="width:100%;padding:8px;border:1px solid var(--sk-border,#ddd);border-radius:6px;"/>
      </label>

      <label style="display:block;">
        <span style="display:block;font-size:13px;color:var(--sk-muted);margin-bottom:4px;">Kind</span>
        <select name="kind" required style="width:100%;padding:8px;border:1px solid var(--sk-border,#ddd);border-radius:6px;">
          ${KINDS.map((k) => `<option value="${k}" ${t.kind === k ? 'selected' : ''}>${k}</option>`).join('')}
        </select>
      </label>

      <label style="display:block;">
        <span style="display:block;font-size:13px;color:var(--sk-muted);margin-bottom:4px;">Organisation</span>
        <select name="organizationId" ${isOrganization ? 'disabled' : ''} style="width:100%;padding:8px;border:1px solid var(--sk-border,#ddd);border-radius:6px;">
          <option value="">- global default -</option>
          ${organizations.map((o) => `<option value="${escapeHtml(o.id)}" ${t.organizationId === o.id ? 'selected' : ''}>${escapeHtml(o.name)}</option>`).join('')}
        </select>
      </label>

      <label style="display:block;">
        <span style="display:block;font-size:13px;color:var(--sk-muted);margin-bottom:4px;">Point type override</span>
        <select name="pointType" style="width:100%;padding:8px;border:1px solid var(--sk-border,#ddd);border-radius:6px;">
          ${POINT_TYPES.map((p) => `<option value="${p}" ${(t.pointType ?? '') === p ? 'selected' : ''}>${p || '- any -'}</option>`).join('')}
        </select>
      </label>

      <div style="grid-column:1 / -1;">
        <span style="display:block;font-size:13px;color:var(--sk-muted);margin-bottom:4px;">Available placeholders - click (or drag) to insert into the subject or body</span>
        <div id="tpl-placeholders" style="display:flex;flex-wrap:wrap;gap:6px;"></div>
      </div>

      <label style="display:block;grid-column:1 / -1;">
        <span style="display:block;font-size:13px;color:var(--sk-muted);margin-bottom:4px;">Subject</span>
        <input name="subject" type="text" required value="${escapeHtml(t.subject)}" style="width:100%;padding:8px;border:1px solid var(--sk-border,#ddd);border-radius:6px;"/>
      </label>

      <div style="grid-column:1 / -1;display:grid;grid-template-columns:1fr 1fr;gap:12px;">
        <label style="display:block;">
          <span style="display:block;font-size:13px;color:var(--sk-muted);margin-bottom:4px;">Body (HTML)</span>
          <textarea name="body" required style="width:100%;height:300px;padding:8px;border:1px solid var(--sk-border,#ddd);border-radius:6px;font-family:monospace;font-size:13px;">${escapeHtml(t.body)}</textarea>
        </label>
        <div>
          <div style="font-size:13px;color:var(--sk-muted);margin-bottom:4px;">Preview</div>
          <iframe id="tpl-preview" sandbox="" style="width:100%;height:300px;border:1px solid var(--sk-border,#ddd);border-radius:6px;background:#fff;"></iframe>
        </div>
      </div>

      <div style="grid-column:1 / -1;display:flex;justify-content:flex-end;gap:8px;">
        <button type="submit" class="btn btn--primary" style="padding:8px 18px;">Save</button>
      </div>
      <div id="tpl-form-error" class="modal__error" style="grid-column:1 / -1;display:none;color:#991b1b;"></div>
    </form>`;

  // Placeholder chips: click inserts {{token}} at the cursor of whichever
  // field (subject or body) was focused last; chips are also natively
  // draggable - the browser handles dropping text/plain into inputs itself.
  const subjectEl = panel.querySelector<HTMLInputElement>('input[name="subject"]')!;
  const kindEl = panel.querySelector<HTMLSelectElement>('select[name="kind"]')!;
  let insertTarget: HTMLInputElement | HTMLTextAreaElement;
  const renderPlaceholderChips = (): void => {
    const box = panel.querySelector<HTMLElement>('#tpl-placeholders')!;
    const names = PLACEHOLDERS_BY_KIND[kindEl.value] ?? [];
    box.innerHTML = names.map((n) => `
      <button type="button" class="tpl-ph" data-token="{{${n}}}" draggable="true" title="Insert {{${n}}}" style="padding:3px 10px;border:1px solid var(--sk-border,#ddd);border-radius:999px;background:var(--sk-blue-50,#e9eef9);font-family:monospace;font-size:12px;cursor:pointer;">{{${n}}}</button>`).join('');
    box.querySelectorAll<HTMLButtonElement>('.tpl-ph').forEach((chip) => {
      chip.addEventListener('click', () => insertToken(chip.dataset.token!));
      chip.addEventListener('dragstart', (e) => {
        e.dataTransfer?.setData('text/plain', chip.dataset.token!);
      });
    });
  };
  const insertToken = (token: string): void => {
    const el = insertTarget;
    const start = el.selectionStart ?? el.value.length;
    const end = el.selectionEnd ?? start;
    el.value = el.value.slice(0, start) + token + el.value.slice(end);
    el.focus();
    el.selectionStart = el.selectionEnd = start + token.length;
    el.dispatchEvent(new Event('input')); // keeps the preview live
  };

  // Live-update preview as the body textarea changes.
  const bodyEl = panel.querySelector<HTMLTextAreaElement>('textarea[name="body"]')!;
  insertTarget = bodyEl;
  subjectEl.addEventListener('focus', () => { insertTarget = subjectEl; });
  bodyEl.addEventListener('focus', () => { insertTarget = bodyEl; });
  kindEl.addEventListener('change', renderPlaceholderChips);
  renderPlaceholderChips();
  const updatePreview = (): void => {
    const iframe = document.getElementById('tpl-preview') as HTMLIFrameElement | null;
    if (!iframe) return;
    iframe.srcdoc = `<!doctype html><html><body style="font-family:Inter,Arial,sans-serif;color:#333;padding:16px;">${sanitizeEmailHtml(bodyEl.value)}</body></html>`;
  };
  bodyEl.addEventListener('input', updatePreview);
  updatePreview();

  panel.querySelector<HTMLButtonElement>('#tpl-delete')?.addEventListener('click', async (e) => {
    if (!editingId) return;
    if (!(await confirmModal({
      title: 'Template verwijderen',
      message: 'Weet u zeker dat u deze e-mailtemplate wilt verwijderen?',
      confirmLabel: 'Verwijderen',
    }))) return;
    const deleteBtn = e.currentTarget as HTMLButtonElement;
    setButtonBusy(deleteBtn, true);
    try {
      const client = await api();
      await client.delete(`/api/admin/email-templates/${encodeURIComponent(editingId)}`);
      editingId = null;
      panel.innerHTML = '<div style="padding:24px;color:var(--sk-muted);">Pick a template on the left or create a new one.</div>';
      await loadTemplates();
    } catch (err) {
      setButtonBusy(deleteBtn, false);
      const box = document.getElementById('tpl-form-error') as HTMLElement | null;
      if (box) {
        box.textContent = err instanceof Error ? err.message : 'Delete failed';
        box.style.display = 'block';
      }
    }
  });

  panel.querySelector<HTMLFormElement>('#tpl-form')!.addEventListener('submit', async (e) => {
    e.preventDefault();
    const form = e.currentTarget as HTMLFormElement;
    const data = new FormData(form);
    const body = {
      id: String(data.get('id') ?? ''),
      name: String(data.get('name') ?? ''),
      kind: String(data.get('kind') ?? ''),
      organizationId: String(data.get('organizationId') ?? '') || null,
      pointType: String(data.get('pointType') ?? '') || null,
      subject: String(data.get('subject') ?? ''),
      body: String(data.get('body') ?? ''),
    };
    const submitBtn = form.querySelector<HTMLButtonElement>('button[type="submit"]');
    setButtonBusy(submitBtn, true);
    try {
      const client = await api();
      if (isNew) await client.post('/api/admin/email-templates', body);
      else await client.put(`/api/admin/email-templates/${encodeURIComponent(t.id)}`, { name: body.name, subject: body.subject, body: body.body });
      editingId = body.id;
      await loadTemplates();
      const reloaded = templates.find((x) => x.id === body.id);
      if (reloaded) renderForm(reloaded);
    } catch (err) {
      const box = document.getElementById('tpl-form-error') as HTMLElement;
      box.textContent = err instanceof Error ? err.message : 'Save failed';
      box.style.display = 'block';
    } finally {
      setButtonBusy(submitBtn, false);
    }
  });
}

function escapeHtml(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

document.getElementById('new-tpl-btn')?.addEventListener('click', renderNew);
const roleSwitcher = document.getElementById('role-switcher')?.parentElement;
if (roleSwitcher) roleSwitcher.style.display = 'none';

await loadOrganizations();
await loadTemplates();
